<template>
    <v-container fluid class="pa-4">
        <!-- Header -->
        <v-row class="mb-2">
            <v-col cols="12" class="d-flex align-center justify-space-between">
                <div>
                    <h1 class="text-h5 font-weight-bold">Safety Events Inbox</h1>
                    <p class="text-body-2 text-medium-emphasis mt-1">Flagged trips across the fleet, ready to coach</p>
                </div>
                <v-btn icon="mdi-refresh" variant="text" :loading="loading" @click="fetchData"/>
            </v-col>
        </v-row>

        <!-- Filters -->
        <v-row class="mb-2">
            <v-col cols="12" sm="4" md="2">
                <v-select v-model="dateRange" :items="dateRangeOptions" item-title="label" item-value="value"
                          label="Time Period" density="compact" variant="outlined" hide-details
                          @update:model-value="reload"/>
            </v-col>
            <v-col cols="12" sm="4" md="2">
                <v-select v-model="eventType" :items="eventTypeOptions" item-title="label" item-value="value"
                          label="Event Type" density="compact" variant="outlined" hide-details
                          @update:model-value="reload"/>
            </v-col>
            <v-col cols="12" sm="4" md="2">
                <v-select v-model="status" :items="statusOptions" item-title="label" item-value="value"
                          label="Status" density="compact" variant="outlined" hide-details
                          @update:model-value="reload"/>
            </v-col>
            <v-col cols="12" sm="4" md="2">
                <v-autocomplete v-model="selectedDriver" :items="drivers" item-title="name" item-value="id"
                                label="Driver" density="compact" variant="outlined" hide-details clearable
                                @update:model-value="reload"/>
            </v-col>
            <v-col cols="12" sm="4" md="2">
                <v-autocomplete v-model="selectedAsset" :items="assets" item-title="label" item-value="id"
                                label="Vehicle" density="compact" variant="outlined" hide-details clearable
                                @update:model-value="reload"/>
            </v-col>
            <v-col cols="12" sm="4" md="2" v-if="isAdmin">
                <company-select v-model="selectedCompany" :items="companies"
                                label="Company" density="compact" variant="outlined" hide-details clearable
                                @update:model-value="onCompanyChange"/>
            </v-col>
        </v-row>

        <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4"/>

        <v-card elevation="2" class="pa-4">
            <div class="d-flex align-center justify-space-between mb-2">
                <h3 class="text-subtitle-1 font-weight-bold">
                    {{ total }} {{ total === 1 ? 'event' : 'events' }}
                </h3>
            </div>
            <!-- Bulk bar: shown while New events are ticked -->
            <v-sheet v-if="selected.length" color="primary" variant="tonal" rounded
                     class="d-flex align-center flex-wrap ga-2 pa-2 mb-2" role="region" aria-label="Selected events">
                <span class="text-body-2 font-weight-medium ms-2">{{ selected.length }} selected</span>
                <v-spacer/>
                <v-btn size="small" variant="flat" color="primary" prepend-icon="mdi-check-all"
                       :disabled="loading" @click="bulkDialog = true">Mark as coached</v-btn>
                <v-btn size="small" variant="text" @click="selected = []">Clear</v-btn>
            </v-sheet>
            <v-table density="compact" hover>
                <thead>
                <tr>
                    <th v-if="canCoach" class="px-1" style="width: 40px;">
                        <v-checkbox-btn :model-value="allSelected" :indeterminate="someSelected"
                                        :disabled="!selectable.length" aria-label="Select all new events on this page"
                                        density="compact" @update:model-value="toggleAll"/>
                    </th>
                    <th>Date</th>
                    <th>Driver</th>
                    <th>Vehicle</th>
                    <th class="text-center">Severity</th>
                    <th class="text-center">Speeding</th>
                    <th class="text-center">Braking</th>
                    <th class="text-center">Accel</th>
                    <th class="text-center">Cornering</th>
                    <th class="text-center">Crash</th>
                    <th class="text-center">Distance</th>
                    <th class="text-center">Status</th>
                    <th class="text-center">Action</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="row in rows" :key="`${row.trip_id}-${row.driver_id}`"
                    :class="{'row-selected': selected.includes(row.id)}">
                    <td v-if="canCoach" class="px-1">
                        <v-checkbox-btn v-if="!row.coached" v-model="selected" :value="row.id" density="compact"
                                        :aria-label="`Select event for ${row.driver_name || 'driver'} on ${fmtDate(row.trip_date)}`"/>
                    </td>
                    <td>{{ fmtDate(row.trip_date) }}</td>
                    <td>{{ row.driver_name || '-' }}</td>
                    <td>{{ row.asset_name || row.vehicle || '-' }}</td>
                    <td class="text-center">
                        <v-chip size="x-small" variant="flat" :color="severityColor(row.severity)">
                            {{ row.severity }}
                        </v-chip>
                    </td>
                    <td class="text-center">{{ row.speeding || '' }}</td>
                    <td class="text-center">{{ row.harsh_braking || '' }}</td>
                    <td class="text-center">{{ row.hard_acceleration || '' }}</td>
                    <td class="text-center">{{ row.sharp_cornering || '' }}</td>
                    <td class="text-center">
                        <v-icon v-if="row.crash" color="error" size="18">mdi-car-emergency</v-icon>
                    </td>
                    <td class="text-center">
                        {{ strOperations().unitifyDistance(row.distance_miles, 'distance', 'miles') }}
                    </td>
                    <td class="text-center">
                        <div class="d-flex flex-column align-center ga-1">
                            <v-chip v-if="row.coached" size="x-small" variant="tonal" color="success">
                                Coached
                            </v-chip>
                            <v-chip v-else size="x-small" variant="tonal" color="warning">New</v-chip>
                            <v-tooltip v-if="row.driver_response" location="top" :disabled="!row.driver_response_note">
                                <template v-slot:activator="{ props }">
                                    <v-chip v-bind="props" size="x-small" variant="flat"
                                            :color="row.driver_response === 'disputed' ? 'error' : 'info'"
                                            :prepend-icon="row.driver_response === 'disputed' ? 'mdi-flag' : 'mdi-check'">
                                        {{ row.driver_response === 'disputed' ? 'Disputed' : 'Acknowledged' }}
                                    </v-chip>
                                </template>
                                <span>{{ row.driver_response_note }}</span>
                            </v-tooltip>
                        </div>
                    </td>
                    <td class="text-center">
                        <v-btn v-if="canCoach" size="x-small" variant="tonal"
                               :color="row.coached ? 'grey' : 'primary'"
                               prepend-icon="mdi-school" @click="goCoach(row)">
                            {{ row.coached ? 'View' : 'Coach' }}
                        </v-btn>
                    </td>
                </tr>
                </tbody>
            </v-table>
            <div v-if="!rows.length && !loading" class="text-center text-medium-emphasis py-6">
                No flagged events for this filter.
            </div>
            <div v-if="lastPage > 1" class="d-flex justify-center mt-4">
                <v-pagination v-model="page" :length="lastPage" :total-visible="7" density="comfortable"
                              @update:model-value="fetchData"/>
            </div>
        </v-card>

        <v-dialog v-model="bulkDialog" max-width="480">
            <v-card>
                <v-card-title class="text-h6">Mark {{ selected.length }} {{ selected.length === 1 ? 'event' : 'events' }} as coached</v-card-title>
                <v-card-text>
                    <p class="text-body-2 mb-3">
                        Creates one coaching entry per driver covering the selected events, and the events leave the New list.
                    </p>
                    <v-textarea v-model="bulkNote" label="Note (optional)" rows="2" auto-grow counter="1000"
                                maxlength="1000" variant="outlined" density="compact"
                                placeholder="For example: discussed in the Monday safety meeting"
                                hint="Each driver sees this note once, on their coaching entry in the Driver app" persistent-hint/>
                </v-card-text>
                <v-card-actions>
                    <v-spacer/>
                    <v-btn variant="text" :disabled="bulkSaving" @click="bulkDialog = false">Cancel</v-btn>
                    <v-btn color="primary" variant="flat" :loading="bulkSaving" :disabled="bulkSaving"
                           @click="markSelectedCoached">Mark as coached</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script setup>
import {ref, computed, onMounted, watch} from 'vue'
import {send} from '@/composables/apiClient'
import {useAuthStore} from '@/stores/authStore'
import {strOperations} from '@/composables/globals'
import {useRoute, useRouter} from 'vue-router'
import CompanySelect from "@/components/globals/company-select.vue";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {refreshMenuCounts} from "@/composables/useMenuCounts";

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const {user} = authStore

const isAdmin = computed(() => user?.type === 'admin')
const loading = ref(false)
const rows = ref([])
const total = ref(0)
const lastPage = ref(1)
const page = ref(1)

const companies = ref([])
const selectedCompany = ref(user?.type !== 'admin' ? user?.company?.id : null)

const drivers = ref([])
const selectedDriver = ref(null)
const assets = ref([])
const selectedAsset = ref(null)

const loadDriversAndAssets = async () => {
    const companyParam = selectedCompany.value ? {company: selectedCompany.value} : {}
    const [driverRes, assetRes] = await Promise.allSettled([
        send('get', 'companies/drivers', {list: true, ...companyParam}),
        send('get', 'assets/list', companyParam),
    ])
    if (driverRes.status === 'fulfilled') {
        const raw = driverRes.value?.data?.data
        const list = Array.isArray(raw) ? raw : (raw?.data ?? [])
        drivers.value = list.map(d => ({...d, name: [d.first_name, d.last_name].filter(Boolean).join(' ')}))
    }
    if (assetRes.status === 'fulfilled') {
        const raw = assetRes.value?.data?.data
        assets.value = Array.isArray(raw) ? raw : []
    }
}

const onCompanyChange = () => {
    selectedDriver.value = null
    selectedAsset.value = null
    loadDriversAndAssets()
    reload()
}

const dateRangeOptions = [
    {label: 'Last 7 Days', value: 7},
    {label: 'Last 30 Days', value: 30},
    {label: 'Last 90 Days', value: 90},
]
// ?days= and ?status= from the menu count, which link to exactly the rows it counted.
const daysFromQuery = () => {
    const n = Number(route.query.days)
    return dateRangeOptions.some(o => o.value === n) ? n : 30
}
const dateRange = ref(daysFromQuery())

const eventTypeOptions = [
    {label: 'All Events', value: 'all'},
    {label: 'Speeding', value: 'speeding'},
    {label: 'Harsh Braking', value: 'braking'},
    {label: 'Hard Acceleration', value: 'acceleration'},
    {label: 'Sharp Cornering', value: 'cornering'},
    {label: 'Crash', value: 'crash'},
]
const eventType = ref('all')

const statusOptions = [
    {label: 'All', value: 'all'},
    {label: 'New', value: 'new'},
    {label: 'Coached', value: 'coached'},
]
const statusFromQuery = () => (statusOptions.some(o => o.value === route.query.status) ? route.query.status : 'all')
const status = ref(statusFromQuery())
// Opened again from the menu while already here: the link's filters, nothing else kept.
watch(() => [route.query.status, route.query.days], () => {
    if (route.name !== 'fleet/reports/safety-inbox') return
    status.value = statusFromQuery()
    dateRange.value = daysFromQuery()
    eventType.value = 'all'
    selectedDriver.value = null
    selectedAsset.value = null
    reload()
})

const severityColor = (s) => {
    if (s === 'high') return 'error'
    if (s === 'medium') return 'warning'
    return 'grey'
}

const fmtDate = (d) => {
    if (!d) return ''
    const dt = new Date(d)
    return isNaN(dt) ? d : dt.toLocaleDateString(undefined, {month: 'short', day: 'numeric', year: 'numeric'})
}

// Open the driver's Coaching tab for the flagged trip. New events pass ?trip so
// the tab opens a pre-filled session; already-coached ones just land on the tab
// where the existing session is listed.
const goCoach = (row) => {
    if (!row?.driver_id) return
    const query = {tab: 'coaching'}
    if (!row.coached && row.trip_id) query.trip = row.trip_id
    router.push({name: 'driver', params: {driver: row.driver_id}, query})
}

// Bulk coaching: only New events on the current page can be ticked
const {showSnackbar} = useSnackbarStore()
const canCoach = computed(() => !user?.is_fleet_driver)
const selected = ref([])
const bulkDialog = ref(false)
const bulkNote = ref('')
const bulkSaving = ref(false)
const selectable = computed(() => rows.value.filter(r => !r.coached && r.id).map(r => r.id))
const allSelected = computed(() => selectable.value.length > 0 && selectable.value.every(id => selected.value.includes(id)))
const someSelected = computed(() => selected.value.length > 0 && !allSelected.value)
const toggleAll = (on) => {
    selected.value = on ? [...selectable.value] : []
}
const markSelectedCoached = async () => {
    bulkSaving.value = true
    try {
        const res = await send('post', 'drivers/safety-events/coached', {
            event_ids: selected.value,
            notes: bulkNote.value.trim() || null,
        })
        const done = res?.data?.data?.coached ?? selected.value.length
        const entries = res?.data?.data?.sessions
        const made = entries == null ? '' : `, ${entries} coaching ${entries === 1 ? 'entry' : 'entries'} created`
        showSnackbar(`${done} ${done === 1 ? 'event' : 'events'} marked as coached${made}`, 'success')
        bulkDialog.value = false
        bulkNote.value = ''
        selected.value = []
        refreshMenuCounts()
        fetchData()
    } catch (e) {
        showSnackbar(e?.response?.data?.message || 'Could not mark the events as coached', 'error')
    } finally {
        bulkSaving.value = false
    }
}

// Filter changes reset to page 1; pagination changes keep the current page.
const reload = () => {
    page.value = 1
    fetchData()
}

const fetchData = async () => {
    loading.value = true
    // The API cuts the last N days on the company's own day (company_timezone), the clock the menu count uses.
    const params = {
        days: dateRange.value,
        event_type: eventType.value,
        status: status.value,
        page: page.value,
    }
    if (selectedCompany.value) params.company = selectedCompany.value
    if (selectedDriver.value) params.driver = selectedDriver.value
    if (selectedAsset.value) params.asset = selectedAsset.value

    try {
        const response = await send('get', 'drivers/safety-events', params)
        const p = response?.data?.data
        rows.value = p?.data ?? []
        // Keep only ticks that are still New rows on this page
        selected.value = selected.value.filter(id => rows.value.some(r => r.id === id && !r.coached))
        lastPage.value = p?.last_page ?? 1
        total.value = p?.total ?? 0
    } catch (e) {
        console.error('Failed to fetch safety events:', e)
        rows.value = []
        selected.value = []
    } finally {
        loading.value = false
    }
}

onMounted(() => {
    fetchData()
    loadDriversAndAssets()
    if (isAdmin.value) {
        send('get', 'companies', {list: true}).then(r => {
            companies.value = r?.data?.data ?? []
        }).catch(e => console.warn('Could not load companies filter', e))
    }
})
</script>

<style scoped>
.row-selected > td {
    background-color: rgba(var(--v-theme-primary), 0.06);
}
</style>
